import {useEffect, useMemo, useState} from 'react';
import {FormProvider, useForm} from 'react-hook-form';
import {useTranslation} from 'react-i18next';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import {Check, FormatListBulleted, LinkPopover} from '@vedmarket/ui-kit';
import cx from 'classnames';
import {useCalculateDeclarationMutation, useUpdateDeclarationMutation} from 'api/calc';
import {tnvedApi} from 'api/tnved';
import {isDemoPage, useGetCalcIdFromRoute} from 'utils/hooks';
import {isCalculationViewModeError, numberWithSpaces} from 'utils/helpers';
import {DECLARATION_FORM} from 'config/constants';
import {useCalculationReadOnly} from 'components/cabinet/calculator/calculation-read-only';
import {useDemoCustomsCollapse} from 'components/cabinet/demo/steps/customs/use-customs';
import {formatReferenceCopy as formatDemoCopy} from 'utils/typography';
import {readOnlyButtonEndIcon} from 'components/common/read-only-action-icon';
import Notification, {NOTIFICATION_TYPE} from 'components/common/notification';
import ModalChooseVat from '../../../../../modal_chooseVat';
import ModalCode from '../../../../../modal_code';
import Preferences from '../../../../../preferences';
import {getTnvedVAT} from '../../../../../../utils';
import CustomsCodeHeader from './code-header';
import CustomsCodeProperties from './code-properties';
import CustomsCertificate from './certificate';
import CustomsProperty from './property';

import './add_form.scss';

const AddForm = ({declaration, index, isCompleted, invoiceId, onSave, onCancel, insideModal, setIsConfirmButtonDisable, onGoalDraftChange}: CustomsCodeFormProps) => {
  const {t, i18n} = useTranslation();

  const orderId = useGetCalcIdFromRoute();
  const readOnly = useCalculationReadOnly();
  const isDemoMode = isDemoPage();
  const {data: tnvedInfo} = tnvedApi.useGetTnvedQuery(declaration.code);
  const [ updateDeclaration, {isLoading: isSaving} ] = useUpdateDeclarationMutation();
  const [ calculateDeclaration ] = useCalculateDeclarationMutation();

  const [ vatRate, setVatRate ] = useState<number | Vat>(declaration.vat_rate_raw?.Value ? declaration.vat_rate_raw : declaration.vat_rate);
  const [ isCollapsed, setIsCollapsed ] = useState(isDemoMode ? false : isCompleted);
  const [ isEditing, setIsEditing ] = useState(!isCompleted);
  const [ openModalCode, setOpenModalCode ] = useState(false);
  const [ openModalChooseVat, setOpenModalChooseVat ] = useState(false);
  const [ showErrorNotification, setShowErrorNotification ] = useState(false);

  const methods = useForm<DeclarationFormFields>({defaultValues: {
    origin_certificate: Boolean(declaration.origin_certificate),
    has_preferences: String(declaration.has_preferences ?? false),
  }});
  const {getValues, reset, handleSubmit, watch} = methods;
  const goalDraftKey = `${invoiceId}-${declaration.country_data.code}-${declaration.code}`;

  const vatOptions = useMemo(() => getTnvedVAT(tnvedInfo), [ tnvedInfo ]);
  const uniqueValues = new Set(vatOptions?.map(item => item.Value));
  const hasUniqValues = uniqueValues.size === vatOptions?.length;
  const hasRawVat = typeof vatRate === 'object' && vatRate !== null;
  const hasRawValue = hasRawVat && Number.isFinite(Number(vatRate.ValueDetail?.ValueCount));
  const hasNumericVat = typeof vatRate === 'number' && Number.isFinite(vatRate);
  const hasSelectedVat = hasUniqValues ? hasNumericVat || hasRawValue : hasRawValue;
  const hasVatRateChoice = (vatOptions?.length || 0) > 1;
  const shouldChooseVat = Boolean(vatOptions?.length) && !hasSelectedVat;
  const showVat = !shouldChooseVat && (hasNumericVat || hasRawVat);

  useEffect(() => {
    const emitValues = () => onGoalDraftChange?.({
      key: goalDraftKey,
      values: isCollapsed ? undefined : {
        origin_certificate: getValues().origin_certificate,
        ...(declaration.allow_preferences && { has_preferences: getValues().has_preferences }),
        ...(showVat && { vat_rate: typeof vatRate === 'number' ? vatRate : vatRate.Value }),
      },
    });
    emitValues();
    const subscription = watch(emitValues);
    return () => {
      subscription.unsubscribe();
      onGoalDraftChange?.({key: goalDraftKey});
    };
  }, [ goalDraftKey, getValues, watch, vatRate, onGoalDraftChange, isCollapsed, declaration.allow_preferences, showVat ]);

  useDemoCustomsCollapse(setIsCollapsed);

  useEffect(() => {
    reset({origin_certificate: Boolean(declaration.origin_certificate), has_preferences: String(declaration.has_preferences ?? false)});
    setVatRate(declaration.vat_rate_raw?.Value ? declaration.vat_rate_raw : declaration.vat_rate);
  }, [ declaration, reset ]);

  useEffect(() => {
    setIsEditing(!isCompleted);

    if (!isDemoMode) {
      setIsCollapsed(isCompleted);
    }
  }, [ isCompleted, isDemoMode ]);

  useEffect(() => {
    setIsConfirmButtonDisable?.(shouldChooseVat);

    return () => setIsConfirmButtonDisable?.(false);
  }, [ shouldChooseVat, setIsConfirmButtonDisable ]);

  const saveValues = async ({values, previewValues}: CustomsDeclarationSaveParams) => {
    if (readOnly) {
      return;
    }

    const params = {orderId, invoiceId, code: declaration.code, country: declaration.country_data.code, ...values};
    const preview = await calculateDeclaration({...params, ...previewValues});

    if ('error' in preview) {
      setShowErrorNotification(true);
      return;
    }

    const result = await updateDeclaration(params);

    if (isCalculationViewModeError(result)) {
      return;
    }

    const hasError = 'error' in result;
    setShowErrorNotification(hasError);

    if (!hasError) {
      onSave();
      onCancel?.();
    }
  };

  const handleChangeFormData = ({field, checked}: CustomsChangeFormDataParams) => {
    const values = getValues();
    const originCertificate = field === DECLARATION_FORM.ORIGIN_CERTIFICATE ? checked : values.origin_certificate;
    const hasPreferences = field === DECLARATION_FORM.HAS_PREFERENCES ? checked : values.has_preferences === 'true';
    const changedValues = {
      ...(field === DECLARATION_FORM.ORIGIN_CERTIFICATE && {origin_certificate: checked}),
      ...(field === DECLARATION_FORM.HAS_PREFERENCES && {has_preferences: checked}),
    };

    onGoalDraftChange?.({
      key: goalDraftKey,
      values: isCollapsed ? undefined : {origin_certificate: originCertificate, ...(declaration.allow_preferences && {has_preferences: String(hasPreferences)}), ...(showVat && {vat_rate: typeof vatRate === 'number' ? vatRate : vatRate.Value})},
    });

    saveValues({
      values: changedValues,
      previewValues: {origin_certificate: originCertificate, has_preferences: hasPreferences},
    });
  };

  const handleChangeVat = async (value: string | Vat) => {
    const values = typeof value === 'string' ? {vat_rate: Number(value)} : {vat_rate_raw: value};
    onGoalDraftChange?.({
      key: goalDraftKey,
      values: isCollapsed ? undefined : {origin_certificate: getValues().origin_certificate, ...(declaration.allow_preferences && {has_preferences: getValues().has_preferences}), vat_rate: typeof value === 'string' ? Number(value) : value.Value},
    });
    await saveValues({values});
  };

  const handleEdit = () => {
    setIsCollapsed(false);
    setIsEditing(true);
  };

  const onSubmit = handleSubmit(values => saveValues({
    values: {
      origin_certificate: values.origin_certificate,
      has_preferences: values.has_preferences === 'true',
    },
  }));

  return <FormProvider {...methods}>
    <form onSubmit={onSubmit} className={cx('customs-declaration', {
      'customs-declaration_collapsed': isCollapsed,
      'customs-declaration_completed': isCompleted,
    })} lang={i18n.resolvedLanguage} aria-busy={isSaving}>
      <CustomsCodeHeader declaration={declaration} index={index} isCompleted={isCompleted && !isEditing} isCollapsed={isCollapsed}
        onToggle={() => setIsCollapsed(value => !value)} onOpenCode={() => setOpenModalCode(true)} onEdit={handleEdit} />
      {!isCollapsed && <>
        <CustomsCodeProperties declaration={declaration} vatRate={vatRate} showVat={showVat} onChooseVat={() => setOpenModalChooseVat(true)} />
        {shouldChooseVat && <div className='customs-declaration__vat-choice'>
          <Button variant='outlined' fullWidth startIcon={<FormatListBulleted />}
            disabled={readOnly || isSaving} endIcon={readOnlyButtonEndIcon(readOnly)} onClick={() => setOpenModalChooseVat(true)}
            title={readOnly ? t('calc__message__lockedByApplication') : undefined}>
            <Typography variant='inherit'>{formatDemoCopy(t('calc__text__customs__choosingTheVatRate'))}</Typography>
          </Button>
          {hasVatRateChoice && <LinkPopover
            linkText={t('calc__help__vatRateLink')}
            text={formatDemoCopy(t('calc__help__vatRate'))}
          />}
        </div>}
        <div className='customs-declaration__certificate-group'>
          <CustomsCertificate isEditing={isEditing} onEdit={handleEdit}
            onChange={checked => handleChangeFormData({field: DECLARATION_FORM.ORIGIN_CERTIFICATE, checked})} />
          <LinkPopover
            linkText={t('calc__help__originCertificateLink')}
            text={formatDemoCopy(t('calc__help__originCertificate'))}
          />
        </div>
        {declaration.allow_preferences && <Preferences code={declaration.code} hasPreferences={declaration.has_preferences} isCompleted={!isEditing}
          handleChangePreferences={checked => handleChangeFormData({field: DECLARATION_FORM.HAS_PREFERENCES, checked})} />}
        <div className='customs-declaration__payment'>
          <CustomsProperty label={t('common__text__customsPayment')} value={`${numberWithSpaces(declaration.subtotal)} USD`} />
        </div>
      </>}
      {onCancel && <div className='customs-declaration__row-buttons'>
        <Button variant='outlined' fullWidth onClick={onCancel}><Typography variant='inherit'>{formatDemoCopy(t('actions__text__cancel'))}</Typography></Button>
        <Button variant='contained' fullWidth type='submit' startIcon={<Check />}><Typography variant='inherit'>{formatDemoCopy(t('actions__text__save'))}</Typography></Button>
      </div>}
      <ModalCode isOpen={openModalCode} onClose={() => setOpenModalCode(false)} insideModal={insideModal} code={declaration.code} tnvedInfo={tnvedInfo} />
      <ModalChooseVat isOpen={openModalChooseVat} onClose={() => setOpenModalChooseVat(false)} insideModal={insideModal}
        vatRate={vatOptions} handleSave={handleChangeVat} currentVatRate={vatRate} hasUniqValues={hasUniqValues} />
      {showErrorNotification && <Notification type={NOTIFICATION_TYPE.ERROR} title={t('errors__message__save')} text={t('errors__message__tryLater5Min')} />}
    </form>
  </FormProvider>;
};

export default AddForm;
